/**
 * SEO Utilities for Dine AI
 * Manages page titles, meta descriptions, and SEO optimization
 */

export interface SEOConfig {
  title: string;
  description?: string;
  keywords?: string[];
}

// SEO-optimized page configurations targeting AI restaurant technology keywords
export const PAGE_SEO_CONFIG: Record<string, SEOConfig> = {
  // Public Pages
  home: {
    title: "Dine AI - AI Menu Assistant & Restaurant Intelligence Platform | Transform Your Menu with AI",
    description: "Revolutionary AI-powered menu management, personalized recommendations, and restaurant insights. Boost revenue 30% with AI menu intelligence and automated customer insights.",
    keywords: ["AI menu assistant", "restaurant AI", "menu intelligence", "AI recommendations", "restaurant technology"]
  },
  pricing: {
    title: "AI Restaurant Pricing Plans | Dine AI Menu Intelligence Platform",
    description: "Affordable AI-powered restaurant solutions starting at $39.99. Get AI menu recommendations, customer insights, and intelligent menu management.",
    keywords: ["restaurant AI pricing", "menu AI cost", "AI restaurant software pricing"]
  },
  enterprise: {
    title: "Enterprise AI Restaurant Solutions | Scale with AI Menu Intelligence",
    description: "Enterprise AI solutions for restaurant chains. Manage 50+ locations with AI-powered menu insights, customer analytics, and automated recommendations.",
    keywords: ["enterprise restaurant AI", "chain restaurant AI", "multi-location AI menu"]
  },
  contact: {
    title: "Contact Dine AI | AI Menu Assistant Support & Demo Requests",
    description: "Get started with AI-powered restaurant technology. Request a demo of our AI menu assistant and intelligent recommendation system.",
    keywords: ["AI restaurant demo", "menu AI support", "restaurant technology contact"]
  },
  about: {
    title: "About Dine AI | Leaders in AI Restaurant Menu Technology",
    description: "Learn how Dine AI is revolutionizing the restaurant industry with AI-powered menu intelligence, automated insights, and personalized dining experiences.",
    keywords: ["AI restaurant company", "menu technology leaders", "restaurant AI innovation"]
  },
  caseStudies: {
    title: "AI Restaurant Success Stories | Real Results with Menu Intelligence",
    description: "Discover how restaurants increased revenue 30% with AI menu recommendations and customer insights. Real success stories from Dine AI partners.",
    keywords: ["AI restaurant case studies", "menu AI success", "restaurant technology results"]
  },
  restaurants: {
    title: "AI Restaurant Solutions | Boost Revenue 30% with Menu Intelligence | Dine AI",
    description: "Transform your restaurant with AI-powered menu optimization, automated insights, and personalized recommendations. Join 500+ restaurants using AI to increase revenue.",
    keywords: ["restaurant AI solutions", "AI menu optimization", "restaurant revenue AI", "menu analytics AI", "restaurant technology"]
  },
  customers: {
    title: "Dine AI for Customers | Personalized AI Menu Recommendations",
    description: "Get personalized menu recommendations powered by AI. Discover dishes you'll love, track favorites, and enjoy a smarter dining experience.",
    keywords: ["AI menu recommendations", "personalized dining", "restaurant app AI", "food recommendations AI", "smart dining experience"]
  },
  "restaurant-staff": {
    title: "Dine AI for Restaurant Staff | Real-Time Order Management & Notifications",
    description: "Empower your restaurant staff with AI-powered order tracking, instant notifications, and seamless table management. Improve service speed by 2x.",
    keywords: ["restaurant staff app", "waiter app", "order management", "table tracking", "real-time notifications", "restaurant technology", "staff efficiency"]
  },
  blog: {
    title: "Restaurant AI Blog | Menu Intelligence Insights & Trends | Dine AI",
    description: "Expert insights on AI in restaurants, menu optimization strategies, and industry trends. Learn how AI is transforming the dining experience.",
    keywords: ["restaurant AI blog", "menu optimization tips", "restaurant technology trends", "AI dining insights", "restaurant industry news"]
  },
  app: {
    title: "Download Dine AI App - AI Dining Assistant | iOS & Android",
    description: "Download Dine AI for iOS and Android. Your personal AI dining assistant for discovering perfect meals with personalized recommendations based on your taste, dietary needs, and mood.",
    keywords: ["dine ai app download", "ai dining app", "restaurant app", "food recommendation app", "personalized menu app", "ios android dining app"]
  },
  "staff-app": {
    title: "Download Dine AI Staff App - Restaurant Order Management | iOS & Android",
    description: "Download Dine AI Staff for iOS and Android. Real-time order management app for restaurant staff with instant notifications and seamless team coordination. Free for restaurant staff.",
    keywords: ["dine ai staff app", "restaurant staff app", "order management app", "waitstaff app", "restaurant team app", "ios android staff app"]
  },
  faq: {
    title: "Dine AI FAQ | Frequently Asked Questions About AI Restaurant Technology",
    description: "Get answers to common questions about Dine AI's AI-powered menu management, subscription plans, features, and restaurant technology solutions.",
    keywords: ["dine ai faq", "restaurant AI questions", "menu AI help", "restaurant technology support", "ai menu assistant faq"]
  },
  terms: {
    title: "Terms of Service | Dine AI Restaurant Intelligence Platform",
    description: "Read Dine AI's Terms of Service for our AI-powered restaurant menu management and intelligence platform.",
    keywords: ["dine ai terms", "terms of service", "restaurant AI legal"]
  },
  virtualHostTerms: {
    title: "Virtual Host Terms of Service | AI Phone Ordering | Dine AI",
    description: "Terms of Service for Dine AI Virtual Host, our AI-powered phone ordering system for restaurants. Covers AI disclaimers, call recording, payments, and partner obligations.",
    keywords: ["virtual host terms", "AI phone ordering terms", "restaurant AI terms", "dine ai virtual host"]
  },
  privacy: {
    title: "Privacy Policy | Dine AI Restaurant Intelligence Platform",
    description: "Learn how Dine AI protects your data and privacy. Our commitment to secure AI-powered restaurant technology.",
    keywords: ["dine ai privacy", "privacy policy", "data protection", "restaurant AI security"]
  },
  cookies: {
    title: "Cookie Policy | Dine AI Restaurant Intelligence Platform",
    description: "Understand how Dine AI uses cookies to improve your experience with our AI-powered restaurant technology.",
    keywords: ["dine ai cookies", "cookie policy", "website cookies"]
  },
  accessibility: {
    title: "Accessibility Statement | Dine AI Restaurant Intelligence Platform",
    description: "Dine AI is committed to making our AI-powered restaurant technology accessible to everyone.",
    keywords: ["dine ai accessibility", "accessibility statement", "inclusive technology"]
  },
  deleteAccount: {
    title: "Delete Account | Dine AI",
    description: "Request deletion of your Dine AI account and associated data.",
    keywords: ["dine ai delete account", "account deletion", "data removal"]
  },

  // Restaurant Dashboard Pages
  dashboard: {
    title: "Restaurant Dashboard | AI Menu Analytics & Insights | Dine AI",
    description: "Access real-time AI-powered menu analytics, customer insights, and performance metrics for your restaurant.",
    keywords: ["restaurant dashboard", "AI menu analytics", "restaurant insights"]
  },
  menuUpload: {
    title: "Upload Menu for AI Processing | Intelligent Menu Creation | Dine AI",
    description: "Upload your restaurant menu for AI-powered processing. Get intelligent categorization, recommendations, and optimization.",
    keywords: ["AI menu upload", "menu digitization", "intelligent menu processing"]
  },
  menuReview: {
    title: "AI Menu Review & Optimization | Smart Menu Management | Dine AI",
    description: "Review and optimize your menu with AI-powered suggestions, pricing insights, and customer preference analysis.",
    keywords: ["AI menu review", "menu optimization", "smart menu management"]
  },
  menuInsights: {
    title: "AI Menu Insights & Analytics | Restaurant Intelligence Platform",
    description: "Get deep AI-powered insights into menu performance, customer preferences, and revenue optimization opportunities.",
    keywords: ["AI menu insights", "restaurant analytics", "menu performance metrics"]
  },
  analytics: {
    title: "AI Restaurant Analytics | Customer Behavior & Menu Intelligence",
    description: "Advanced AI analytics for restaurants. Track customer behavior, menu performance, and get predictive insights.",
    keywords: ["AI restaurant analytics", "customer behavior analysis", "predictive menu insights"]
  },
  customerInsights: {
    title: "AI Customer Preference Insights | Personalized Menu Recommendations",
    description: "Understand customer preferences with AI. Deliver personalized menu recommendations and boost satisfaction.",
    keywords: ["AI customer insights", "personalized recommendations", "customer preference analysis"]
  },
  
  // Authentication Pages
  login: {
    title: "Login | Dine AI Restaurant Intelligence Platform",
    description: "Access your AI-powered restaurant dashboard and menu management tools.",
    keywords: ["restaurant login", "AI platform access"]
  },
  signup: {
    title: "Sign Up for AI Restaurant Technology | Start Free Trial | Dine AI",
    description: "Join thousands of restaurants using AI for menu optimization. Start your 7-day free trial today.",
    keywords: ["AI restaurant signup", "free trial", "menu AI registration"]
  },
  demo: {
    title: "Try Dine AI Demo | Experience AI Menu Intelligence Live",
    description: "Experience the power of AI menu management with our interactive demo. See real recommendations and insights.",
    keywords: ["AI restaurant demo", "menu AI trial", "restaurant technology demo"]
  },
  
  // Settings & Management
  settings: {
    title: "Restaurant Settings | Manage AI Features & Subscription | Dine AI",
    description: "Configure your AI-powered restaurant features, manage subscriptions, and customize menu intelligence settings.",
    keywords: ["restaurant settings", "AI configuration", "menu management settings"]
  },
  seasonalMenu: {
    title: "AI Seasonal Menu Creator | Smart Menu Variations | Dine AI",
    description: "Create seasonal menus with AI-powered suggestions based on trends, weather, and customer preferences.",
    keywords: ["seasonal menu AI", "menu variations", "smart menu creation"]
  },
  menuImages: {
    title: "AI-Generated Menu Images | Professional Food Photography | Dine AI",
    description: "Generate stunning menu images with AI. Professional food photography without the photoshoot.",
    keywords: ["AI food images", "menu photography", "AI-generated food photos"]
  },

  // Virtual Host Pages
  vhDashboard: {
    title: "AI Host Control Center | Dine AI",
    description: "Manage AI phone ordering, view live orders, and monitor call activity for your restaurant.",
    keywords: ["AI phone ordering", "restaurant control center", "virtual host dashboard"]
  },
  vhSettings: {
    title: "AI Host Settings | Dine AI",
    description: "Configure your AI Host voice, greetings, operating hours, and payment settings.",
    keywords: ["AI host settings", "virtual host configuration", "restaurant AI setup"]
  },
  vhCallLog: {
    title: "Call Log | Dine AI",
    description: "Review AI phone call transcripts, order history, and call analytics.",
    keywords: ["call log", "AI call transcripts", "phone order history"]
  },
  vhStaffDashboard: {
    title: "Staff Dashboard | Dine AI",
    description: "Restaurant staff order management and notification dashboard.",
    keywords: ["staff dashboard", "order management", "restaurant staff"]
  },
  vhStaffLogin: {
    title: "Staff Login | Dine AI",
    description: "Sign in to your restaurant staff dashboard.",
    keywords: ["staff login", "restaurant staff access"]
  }
};

/**
 * Set the page title and meta tags for SEO
 * @param pageKey - The key from PAGE_SEO_CONFIG
 * @param customTitle - Optional custom title to override default
 */
export function setPageSEO(pageKey: string, customTitle?: string): void {
  const config = PAGE_SEO_CONFIG[pageKey] || PAGE_SEO_CONFIG.home;
  
  // Set title
  document.title = customTitle || config.title;
  
  // Set or update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  if (config.description) {
    metaDescription.setAttribute('content', config.description);
  }
  
  // Set or update meta keywords
  if (config.keywords && config.keywords.length > 0) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', config.keywords.join(', '));
  }
  
  // Update Open Graph tags for social sharing
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', customTitle || config.title);
  }
  
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription && config.description) {
    ogDescription.setAttribute('content', config.description);
  }
}

/**
 * Custom hook for setting page SEO in React components
 */
export function usePageSEO(pageKey: string, customTitle?: string): void {
  if (typeof window !== 'undefined') {
    setPageSEO(pageKey, customTitle);
  }
}